// src/AuthGate.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabaseClient";

export default function AuthGate({ children }) {
  const origin = useMemo(() => window.location.origin, []);

  const [loading, setLoading] = useState(true);
  const [checkingInvite, setCheckingInvite] = useState(false);

  const [session, setSession] = useState(null);
  const [inviteState, setInviteState] = useState("unknown"); // unknown | not_invited | pending | approved

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [info, setInfo] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Clean callback URL
  useEffect(() => {
    if (window.location.pathname.startsWith("/auth/callback")) {
      setTimeout(() => window.history.replaceState({}, "", "/"), 0);
    }
  }, []);

  // Load session + subscribe
  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      const { data, error } = await supabase.auth.getSession();
      if (!mounted) return;

      if (error) console.error("getSession error:", error);
      setSession(data?.session ?? null);
      setLoading(false);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession ?? null);
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  // Invite check when session changes
  useEffect(() => {
    let cancelled = false;

    async function checkInvite() {
      if (!session?.user?.id) {
        setInviteState("unknown");
        return;
      }

      setCheckingInvite(true);
      setErrorMsg("");

      const { data, error } = await supabase
        .from("org_members")
        .select("approved")
        .eq("user_id", session.user.id)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        console.error("org_members lookup failed:", error);
        setInviteState("not_invited");
        setErrorMsg("Access check failed. Please contact your admin.");
        setCheckingInvite(false);
        return;
      }

      if (!data) {
        setInviteState("not_invited");
        setCheckingInvite(false);
        return;
      }

      if (!data.approved) {
        setInviteState("pending");
        setCheckingInvite(false);
        return;
      }

      setInviteState("approved");
      setCheckingInvite(false);
    }

    checkInvite();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  // Actions
  const signInWithPassword = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setInfo("");

    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !password) {
      setErrorMsg("Enter your email and password.");
      return;
    }

    const { error } = await supabase.auth.signInWithPassword({
      email: cleanEmail,
      password,
    });

    if (error) {
      console.error("signInWithPassword error:", error);
      setErrorMsg("Sign-in failed. Check your email/password and try again.");
      return;
    }

    // session listener will update UI
  };

  const sendPasswordRecovery = async () => {
    setErrorMsg("");
    setInfo("");

    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setErrorMsg("Enter your email above first, then click Forgot password.");
      return;
    }

    const { error } = await supabase.auth.resetPasswordForEmail(cleanEmail, {
      redirectTo: `${origin}/auth/callback`,
    });

    if (error) {
      console.error("resetPasswordForEmail error:", error);
      setErrorMsg(error.message);
      return;
    }

    setInfo("Password recovery email sent — check your inbox (and spam/junk).");
  };

  const signOut = async () => {
    setErrorMsg("");
    setInfo("");
    await supabase.auth.signOut();
    window.history.replaceState({}, "", "/");
  };

  // UI
  if (loading) {
    return (
      <CenteredCard>
        <BrandHeader />
        <h2 style={styles.h2}>Loading…</h2>
        <p style={styles.p}>Checking session</p>
      </CenteredCard>
    );
  }

  // ✅ SIGNED OUT SCREEN (email + password)
  if (!session) {
    return (
      <CenteredCard>
        <BrandHeader />
        <h2 style={styles.h2}>Secure Access</h2>
        <div style={styles.subtle}>Invite-only</div>

        <form onSubmit={signInWithPassword} style={{ width: "100%", marginTop: 18 }}>
          <label style={styles.label}>Email</label>
          <input
            style={styles.input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
          />

          <label style={{ ...styles.label, marginTop: 12 }}>Password</label>
          <input
            style={styles.input}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="current-password"
            type="password"
          />

          <button type="submit" style={{ ...styles.btn, marginTop: 14 }}>
            Sign in
          </button>

          <button
            type="button"
            onClick={sendPasswordRecovery}
            style={{ ...styles.linkBtn, marginTop: 10 }}
          >
            Forgot password?
          </button>

          {info ? <div style={styles.infoBox}>{info}</div> : null}
          {errorMsg ? <div style={styles.errorBox}>{errorMsg}</div> : null}

          <div style={styles.tip}>If you need access, contact your admin.</div>
        </form>

        <Footer />
      </CenteredCard>
    );
  }

  // Signed in but checking membership
  if (checkingInvite || inviteState === "unknown") {
    return (
      <CenteredCard>
        <BrandHeader />
        <h2 style={styles.h2}>Loading…</h2>
        <p style={styles.p}>Checking access</p>
        <Footer />
      </CenteredCard>
    );
  }

  // Signed in but NOT INVITED
  if (inviteState === "not_invited") {
    return (
      <CenteredCard>
        <BrandHeader />
        <h2 style={styles.h2}>Invite-only access</h2>
        <p style={styles.p}>Your account isn’t on the invite list.</p>
        {errorMsg ? <div style={styles.errorBox}>{errorMsg}</div> : null}

        <button onClick={signOut} style={{ ...styles.btn, marginTop: 14, border: "2px solid #e18b8b" }}>
          Sign out
        </button>

        <div style={styles.tip}>Ask an admin to add your email to the invite list.</div>
        <Footer />
      </CenteredCard>
    );
  }

  // Signed in but PENDING approval
  if (inviteState === "pending") {
    return (
      <CenteredCard>
        <BrandHeader />
        <h2 style={styles.h2}>Approval pending</h2>
        <p style={styles.p}>Your account exists but isn’t approved yet.</p>

        <button onClick={signOut} style={{ ...styles.btn, marginTop: 14, border: "2px solid #e18b8b" }}>
          Sign out
        </button>

        <div style={styles.tip}>Contact an admin to approve your access.</div>
        <Footer />
      </CenteredCard>
    );
  }

  // ✅ APPROVED
  return children;
}

/* ---------------------------
   Presentational components
---------------------------- */
function CenteredCard({ children }) {
  return (
    <div style={styles.bg}>
      <div style={styles.card}>{children}</div>
    </div>
  );
}

function BrandHeader() {
  return (
    <div style={{ display: "flex", justifyContent: "center", marginTop: 24, marginBottom: 18 }}>
      <img src="/carepathwelllong.png" alt="CarePathWell" style={{ maxWidth: 520, width: "80%" }} />
    </div>
  );
}

function Footer() {
  return <div style={styles.footer}>© 2026 CarePathWell. All rights reserved.</div>;
}

/* ---------------------------
   Styles
---------------------------- */
const styles = {
  bg: {
    minHeight: "100vh",
    background: "#eef6f7",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "40px 16px",
  },
  card: {
    width: "min(980px, 100%)",
    minHeight: "min(640px, 90vh)",
    background: "#fff",
    borderRadius: 18,
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
    padding: "24px 22px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  h2: {
    fontSize: 44,
    lineHeight: 1.05,
    margin: "10px 0 6px",
    fontWeight: 800,
    textAlign: "center",
    color: "#0f2f2a",
  },
  p: {
    margin: 0,
    fontSize: 22,
    textAlign: "center",
    color: "#28423e",
  },
  subtle: {
    marginTop: 4,
    fontSize: 18,
    opacity: 0.75,
    textAlign: "center",
  },
  label: {
    display: "block",
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 6,
    color: "#0f2f2a",
  },
  input: {
    width: "100%",
    fontSize: 16,
    padding: "12px 14px",
    borderRadius: 10,
    border: "1px solid rgba(0,0,0,0.15)",
    outline: "none",
  },
  btn: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.15)",
    background: "linear-gradient(90deg, #d7e6ff, #e6f7d7)",
    fontSize: 16,
    fontWeight: 800,
    cursor: "pointer",
  },
  linkBtn: {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.10)",
    background: "#fff",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
  },
  errorBox: {
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 10,
    background: "#ffe8e8",
    border: "1px solid #f3b2b2",
    color: "#7a1f1f",
    fontSize: 14,
  },
  infoBox: {
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 10,
    background: "#eef7ff",
    border: "1px solid #b9daf6",
    color: "#184a7a",
    fontSize: 14,
  },
  tip: {
    marginTop: 14,
    fontSize: 14,
    opacity: 0.8,
    textAlign: "center",
  },
  footer: {
    marginTop: "auto",
    paddingTop: 18,
    fontSize: 14,
    opacity: 0.7,
    textAlign: "center",
  },
};
